import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { ordenesCompraApi, cotizacionesApi, productosApi, receptoresApi, presupuestoApi } from '../../utils/api';
import SelectorCuenta, { SelectorCentro } from '../presupuesto/SelectorCuenta';
import ModalExcedido from '../presupuesto/ModalExcedido';
import api from '../../utils/api';
import { getUbicacionCompleta } from '../../utils/costaRicaLocations';

export default function CrearOrdenCompra() {
  const navigate = useNavigate();
  const { cotizacionId, id: editId } = useParams();
  const isFromCotizacion = !!cotizacionId;
  const isEditing = !!editId;

  const [saving, setSaving] = useState(false);
  const [cotizacion, setCotizacion] = useState(null);
  const [proveedores, setProveedores] = useState([]);
  const [proveedorSearch, setProveedorSearch] = useState('');
  const [showProveedorDropdown, setShowProveedorDropdown] = useState(false);
  // End User: se busca en Clientes (receptores) y se rellenan los campos; siguen editables
  const [endUserSearch, setEndUserSearch] = useState('');
  const [endUserResults, setEndUserResults] = useState([]);
  const [showEndUserDropdown, setShowEndUserDropdown] = useState(false);
  const [endUserCliente, setEndUserCliente] = useState(null);

  const [form, setForm] = useState({
    proveedorId: null,
    proveedorNombre: '',
    codigoMoneda: 'USD',
    notas: '',
    // Proveedor contact
    atencion: '', deliverTo: 'CR', confirmTo: '', transporte: '', terminoPago: 'PREPAID',
    // User Account
    accountName: '', accountNumber: '', accountContact: '', accountPhone: '', accountEmail: '', accountAddress: '', accountWeb: '',
    // End User
    endUserName: '', endUserAddress: '', endUserCity: '', endUserContact: '', endUserPhone: '', endUserEmail: '', endUserProvisionEmail: '',
    // Other
    quoteId: '', observaciones: ''
  });

  const [lineas, setLineas] = useState([]);
  const [selectedLineas, setSelectedLineas] = useState(new Set());
  const [productoSearch, setProductoSearch] = useState('');
  const [productoResults, setProductoResults] = useState([]);
  const [activeLineId, setActiveLineId] = useState(null);
  // Presupuesto: cuenta y centro por línea (comprometido y control de compras). Sin permiso
  // de ver el catálogo, la columna no se muestra y la OC se guarda como antes.
  const [catalogo, setCatalogo] = useState(null);
  const [excedido, setExcedido] = useState(null); // { error, controlPresupuesto, puedeExceder }

  useEffect(() => {
    presupuestoApi.catalogoContable().then(r => setCatalogo(r.data.data)).catch(() => setCatalogo(null));
  }, []);

  // Load data based on mode
  useEffect(() => {
    if (isFromCotizacion) loadCotizacion();
    if (isEditing) loadOrdenCompra();
  }, [cotizacionId, editId]);

  const loadOrdenCompra = async () => {
    try {
      const res = await ordenesCompraApi.getById(editId);
      const oc = res.data?.data;
      if (!oc) { toast.error('OC no encontrada'); navigate('/compras/ordenes-compra'); return; }
      setForm({
        proveedorId: oc.proveedorId, proveedorNombre: oc.proveedorNombre, codigoMoneda: oc.codigoMoneda, notas: oc.notas || '',
        atencion: oc.atencion || '', deliverTo: oc.deliverTo || '', confirmTo: oc.confirmTo || '',
        transporte: oc.transporte || '', terminoPago: oc.terminoPago || '',
        accountName: oc.accountName || '', accountNumber: oc.accountNumber || '',
        accountContact: oc.accountContact || '', accountPhone: oc.accountPhone || '',
        accountEmail: oc.accountEmail || '', accountAddress: oc.accountAddress || '', accountWeb: oc.accountWeb || '',
        endUserName: oc.endUserName || '', endUserAddress: oc.endUserAddress || '', endUserCity: oc.endUserCity || '',
        endUserContact: oc.endUserContact || '', endUserPhone: oc.endUserPhone || '',
        endUserEmail: oc.endUserEmail || '', endUserProvisionEmail: oc.endUserProvisionEmail || '',
        quoteId: oc.quoteId || '', observaciones: oc.observaciones || ''
      });
      setProveedorSearch(oc.proveedorNombre);
      setLineas((oc.lineas || []).map(l => ({
        id: `oc-${l.id}`,
        cotizacionLineaId: l.cotizacionLineaId,
        codigo: l.codigo || '',
        detalle: l.detalle || '',
        cantidad: l.cantidad,
        costoUnitario: l.costoUnitario,
        impuestoTarifa: l.impuestoTarifa != null ? l.impuestoTarifa : 13,
        cuentaId: l.cuentaId || null,
        centroCostoId: l.centroCostoId || null,
        selected: true
      })));
      setSelectedLineas(new Set((oc.lineas || []).map(l => `oc-${l.id}`)));
    } catch (error) {
      toast.error('Error cargando OC');
    }
  };

  const loadCotizacion = async () => {
    try {
      const res = await cotizacionesApi.getById(cotizacionId);
      const cot = res.data?.data;
      setCotizacion(cot);
      setForm(prev => ({
        ...prev,
        codigoMoneda: cot.codigoMoneda || 'USD',
        // Pre-fill End User from cotizacion client
        endUserName: cot.clienteNombre || cot.receptorNombre || '',
        endUserPhone: cot.clienteTelefono || '',
        endUserEmail: cot.clienteEmail || '',
      }));
      // Pre-fill lines from cotizacion
      const cotLineas = (cot.lineas || []).map((l, i) => ({
        id: `cot-${l.id}`,
        cotizacionLineaId: l.id,
        codigo: l.codigo || '',
        detalle: l.detalle || '',
        cantidad: l.cantidad || 1,
        costoUnitario: '', // User must enter cost
        impuestoTarifa: l.impuestoTarifa != null ? l.impuestoTarifa : 13,
        precioVenta: l.precioUnitario || 0, // Show for reference
        selected: true
      }));
      setLineas(cotLineas);
      setSelectedLineas(new Set(cotLineas.map(l => l.id)));
    } catch (error) {
      toast.error('Error cargando cotización');
      console.error(error);
    }
  };

  // Search productos for standalone mode (by name or code)
  useEffect(() => {
    if (!activeLineId || productoSearch.length < 2) { setProductoResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await productosApi.list({ q: productoSearch, limit: 10 });
        setProductoResults(res.data?.data || []);
      } catch (e) { console.error(e); }
    }, 250);
    return () => clearTimeout(timer);
  }, [productoSearch, activeLineId]);

  const selectProducto = (producto, lineId) => {
    setLineas(prev => prev.map(l => l.id === lineId ? {
      ...l,
      productoId: producto.id,
      codigo: producto.sku || producto.codigo || '',
      detalle: producto.nombre || producto.detalle || '',
      costoUnitario: '',
      impuestoTarifa: producto.impuestoTarifa ?? producto.impuesto_tarifa ?? 13
    } : l));
    setProductoResults([]);
    setActiveLineId(null);
    setProductoSearch('');
  };

  // Search proveedores
  useEffect(() => {
    if (proveedorSearch.length < 2) { setProveedores([]); return; }
    // Don't re-search if the search matches the already selected proveedor
    if (form.proveedorId && proveedorSearch === form.proveedorNombre) return;
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/gastos/proveedores?q=${encodeURIComponent(proveedorSearch)}&limit=10`);
        setProveedores(res.data?.data || []);
        setShowProveedorDropdown(true);
      } catch (e) { console.error(e); }
    }, 300);
    return () => clearTimeout(timer);
  }, [proveedorSearch]);

  // Search clientes para End User
  useEffect(() => {
    if (endUserSearch.trim().length < 2) { setEndUserResults([]); setShowEndUserDropdown(false); return; }
    if (endUserCliente && endUserSearch === endUserCliente.nombre) return;
    const timer = setTimeout(async () => {
      try {
        const res = await receptoresApi.list({ q: endUserSearch.trim(), limit: 10 });
        setEndUserResults(res.data?.data || []);
        setShowEndUserDropdown(true);
      } catch (e) { console.error(e); }
    }, 300);
    return () => clearTimeout(timer);
  }, [endUserSearch]);

  const selectEndUserCliente = (c) => {
    const telefono = c.telefonoNumero || c.telefono_numero || '';
    const codigo = c.telefonoCodigo || c.telefono_codigo || '';
    const ciudad = getUbicacionCompleta(c.provincia, c.canton, c.distrito);
    setForm(prev => ({
      ...prev,
      endUserName: c.nombre || '',
      endUserAddress: c.otrasSenas || c.otras_senas || prev.endUserAddress,
      endUserCity: ciudad || prev.endUserCity,
      endUserContact: c.nombreComercial || c.nombre_comercial || prev.endUserContact,
      endUserPhone: telefono ? (codigo && codigo !== '506' ? `+${codigo} ${telefono}` : telefono) : prev.endUserPhone,
      endUserEmail: c.email || c.emailFacturacion || c.email_facturacion || prev.endUserEmail,
      endUserProvisionEmail: c.emailFacturacion || c.email_facturacion || c.email || prev.endUserProvisionEmail
    }));
    setEndUserCliente(c);
    setEndUserSearch(c.nombre || '');
    setShowEndUserDropdown(false);
  };

  const selectProveedor = async (p) => {
    setForm(prev => ({ ...prev, proveedorId: p.id, proveedorNombre: p.nombre }));
    setProveedorSearch(p.nombre);
    setShowProveedorDropdown(false);
    // Pre-fill from last OC for this proveedor
    try {
      const res = await ordenesCompraApi.getLastByProveedor(p.id);
      const last = res.data?.data;
      if (last) {
        setForm(prev => ({
          ...prev,
          codigoMoneda: last.codigo_moneda || prev.codigoMoneda,
          atencion: last.atencion || '', deliverTo: last.deliver_to || '', confirmTo: last.confirm_to || '',
          transporte: last.transporte || '', terminoPago: last.termino_pago || '',
          accountName: last.account_name || '', accountNumber: last.account_number || '',
          accountContact: last.account_contact || '', accountPhone: last.account_phone || '',
          accountEmail: last.account_email || '', accountAddress: last.account_address || '', accountWeb: last.account_web || '',
        }));
      }
    } catch (e) { /* silently ignore - just won't pre-fill */ }
  };

  const addLinea = () => {
    const id = `new-${Date.now()}`;
    setLineas(prev => [...prev, {
      id, cotizacionLineaId: null, codigo: '', detalle: '', cantidad: 1,
      costoUnitario: '', impuestoTarifa: 13, selected: true
    }]);
    setSelectedLineas(prev => new Set([...prev, id]));
  };

  const removeLinea = (id) => {
    setLineas(prev => prev.filter(l => l.id !== id));
    setSelectedLineas(prev => { const n = new Set(prev); n.delete(id); return n; });
  };

  const updateLinea = (id, field, value) => {
    setLineas(prev => prev.map(l => l.id === id ? { ...l, [field]: value } : l));
  };

  const toggleLinea = (id) => {
    setSelectedLineas(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  // Calculate totals from selected lines
  const selectedLines = lineas.filter(l => selectedLineas.has(l.id));
  const totals = selectedLines.reduce((acc, l) => {
    const sub = (parseFloat(l.cantidad) || 0) * (parseFloat(l.costoUnitario) || 0);
    const imp = Math.round(sub * (parseFloat(l.impuestoTarifa) || 0) / 100 * 100) / 100;
    return { subtotal: acc.subtotal + sub, impuesto: acc.impuesto + imp, total: acc.total + sub + imp };
  }, { subtotal: 0, impuesto: 0, total: 0 });

  const sym = form.codigoMoneda === 'USD' ? '$' : '₡';

  const handleSave = async (motivo = null) => {
    if (!form.proveedorId) { toast.error('Seleccione un proveedor'); return; }
    if (selectedLines.length === 0) { toast.error('Seleccione al menos una línea'); return; }
    if (selectedLines.some(l => !l.costoUnitario || parseFloat(l.costoUnitario) <= 0)) {
      toast.error('Ingrese el costo unitario de todas las líneas seleccionadas'); return;
    }

    setSaving(true);
    try {
      const lineasData = selectedLines.map(l => ({
        cotizacionLineaId: l.cotizacionLineaId || null,
        codigo: l.codigo,
        detalle: l.detalle,
        cantidad: parseFloat(l.cantidad) || 1,
        costoUnitario: parseFloat(l.costoUnitario) || 0,
        impuestoTarifa: parseFloat(l.impuestoTarifa) || 0,
        cuentaId: l.cuentaId || null,
        centroCostoId: l.centroCostoId || null
      }));

      const ocData = {
        proveedorId: form.proveedorId,
        codigoMoneda: form.codigoMoneda,
        notas: form.notas,
        lineas: lineasData,
        atencion: form.atencion, deliverTo: form.deliverTo, confirmTo: form.confirmTo,
        transporte: form.transporte, terminoPago: form.terminoPago,
        accountName: form.accountName, accountNumber: form.accountNumber,
        accountContact: form.accountContact, accountPhone: form.accountPhone,
        accountEmail: form.accountEmail, accountAddress: form.accountAddress, accountWeb: form.accountWeb,
        endUserName: form.endUserName, endUserAddress: form.endUserAddress, endUserCity: form.endUserCity,
        endUserContact: form.endUserContact, endUserPhone: form.endUserPhone,
        endUserEmail: form.endUserEmail, endUserProvisionEmail: form.endUserProvisionEmail,
        quoteId: form.quoteId, observaciones: form.observaciones,
        motivoExceso: motivo || undefined
      };

      let guardadas;
      if (isEditing) {
        guardadas = [(await ordenesCompraApi.update(editId, ocData)).data?.data];
        toast.success('Orden de compra actualizada');
      } else if (isFromCotizacion) {
        guardadas = (await ordenesCompraApi.createFromCotizacion({
          cotizacionId: parseInt(cotizacionId),
          grupos: [{ proveedorId: form.proveedorId, lineas: lineasData, notas: form.notas, ...ocData }]
        })).data?.data || [];
        toast.success('Orden de compra creada');
      } else {
        guardadas = [(await ordenesCompraApi.create(ocData)).data?.data];
        toast.success('Orden de compra creada');
      }
      // Control de compras en modo avisar (o excepción autorizada): se guardó, pero se avisa.
      for (const oc of guardadas.filter(Boolean)) {
        const cp = oc.controlPresupuesto;
        if (cp?.resultado === 'aviso') toast(`Presupuesto: ${cp.mensajes.join(' · ')}`, { icon: '⚠️', duration: 8000 });
        if (cp?.resultado === 'excepcion') toast('Exceso de presupuesto autorizado y registrado', { icon: '✔️', duration: 5000 });
      }
      setExcedido(null);
      navigate('/compras/ordenes-compra');
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.codigo === 'PRESUPUESTO_EXCEDIDO') {
        setExcedido(error.response.data);
      } else {
        toast.error(error.response?.data?.error || 'Error creando orden de compra');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/compras/ordenes-compra')} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold">
            {isEditing ? 'Editar Orden de Compra' : isFromCotizacion ? 'Generar Orden de Compra' : 'Nueva Orden de Compra'}
          </h1>
          {cotizacion && (
            <p className="text-sm text-gray-500">Desde cotización {cotizacion.numero} - {cotizacion.receptorNombre || cotizacion.clienteNombre}</p>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border p-6 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Proveedor */}
          <div className="relative col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Proveedor *</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={proveedorSearch}
                onChange={(e) => { setProveedorSearch(e.target.value); setForm(prev => ({ ...prev, proveedorId: null, proveedorNombre: '' })); }}
                placeholder="Buscar proveedor..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {showProveedorDropdown && proveedores.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {proveedores.map(p => (
                  <button key={p.id} onClick={() => selectProveedor(p)}
                    className="w-full text-left px-4 py-2 hover:bg-blue-50 text-sm border-b last:border-0">
                    <span className="font-medium">{p.nombre}</span>
                    <span className="text-gray-400 ml-2">{p.identificacion}</span>
                  </button>
                ))}
              </div>
            )}
            {form.proveedorId && (
              <p className="text-xs text-green-600 mt-1">Proveedor seleccionado: {form.proveedorNombre}</p>
            )}
          </div>

          {/* Moneda */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Moneda</label>
            <select value={form.codigoMoneda} onChange={(e) => setForm(prev => ({ ...prev, codigoMoneda: e.target.value }))}
              className="w-full py-2 px-3 border rounded-lg" disabled={isFromCotizacion}>
              <option value="USD">USD - Dólar</option>
              <option value="CRC">CRC - Colón</option>
            </select>
          </div>
        </div>

        {/* Proveedor Contact Details */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {[
            ['atencion', 'Atención / Attention'],
            ['deliverTo', 'Puesto en / Deliver To'],
            ['confirmTo', 'Confirmar / Confirm To'],
            ['terminoPago', 'Término Pago'],
            ['transporte', 'Transporte / Shipped by'],
            ['quoteId', 'Quote ID / Referencia']
          ].map(([key, label]) => (
            <div key={key}>
              <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
              <input type="text" value={form[key]} onChange={(e) => setForm(prev => ({ ...prev, [key]: e.target.value }))}
                className="w-full border rounded-lg px-2 py-1.5 text-sm" />
            </div>
          ))}
        </div>

        {/* User Account Information */}
        <details className="mb-4 border rounded-lg">
          <summary className="px-4 py-2 bg-gray-50 cursor-pointer font-semibold text-sm text-gray-700 rounded-t-lg">User Account Information</summary>
          <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ['accountName', 'Account Name'],
              ['accountNumber', 'Account Number'],
              ['accountContact', 'Contacto'],
              ['accountPhone', 'Teléfono'],
              ['accountEmail', 'Email'],
              ['accountAddress', 'Dirección'],
              ['accountWeb', 'Página Web']
            ].map(([key, label]) => (
              <div key={key}>
                <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                <input type="text" value={form[key]} onChange={(e) => setForm(prev => ({ ...prev, [key]: e.target.value }))}
                  className="w-full border rounded-lg px-2 py-1.5 text-sm" />
              </div>
            ))}
          </div>
        </details>

        {/* End User License Information */}
        <details className="mb-4 border rounded-lg">
          <summary className="px-4 py-2 bg-gray-50 cursor-pointer font-semibold text-sm text-gray-700 rounded-t-lg">End User License Information</summary>
          <div className="px-4 pt-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">Buscar en Clientes</label>
            <div className="relative md:w-1/2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={endUserSearch}
                onChange={(e) => { setEndUserSearch(e.target.value); if (endUserCliente) setEndUserCliente(null); }}
                onFocus={() => { if (endUserResults.length > 0 && !endUserCliente) setShowEndUserDropdown(true); }}
                placeholder="Nombre o identificación del cliente..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
              {showEndUserDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {endUserResults.length === 0 ? (
                    <p className="px-4 py-2 text-sm text-gray-400">Sin resultados</p>
                  ) : endUserResults.map(c => (
                    <button key={c.id} type="button" onClick={() => selectEndUserCliente(c)}
                      className="w-full text-left px-4 py-2 hover:bg-blue-50 text-sm border-b last:border-0">
                      <span className="font-medium">{c.nombre}</span>
                      <span className="text-gray-400 ml-2">{c.identificacion}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {endUserCliente && (
              <p className="text-xs text-green-600 mt-1">Datos tomados del cliente {endUserCliente.nombre}. Se pueden ajustar abajo.</p>
            )}
          </div>
          <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ['endUserName', 'Nombre'],
              ['endUserAddress', 'Dirección'],
              ['endUserCity', 'Ciudad'],
              ['endUserContact', 'Contacto'],
              ['endUserPhone', 'Teléfono'],
              ['endUserEmail', 'Email'],
              ['endUserProvisionEmail', 'Email Aprovisionamiento']
            ].map(([key, label]) => (
              <div key={key}>
                <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                <input type="text" value={form[key]} onChange={(e) => setForm(prev => ({ ...prev, [key]: e.target.value }))}
                  className="w-full border rounded-lg px-2 py-1.5 text-sm" />
              </div>
            ))}
          </div>
        </details>

        {/* Lines */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-700">Líneas</h3>
            {catalogo && lineas.length > 1 && (
              <div className="flex items-center gap-2 text-xs text-gray-500 ml-auto mr-3 w-80">
                <span className="whitespace-nowrap">Cuenta para todas:</span>
                <div className="flex-1"><SelectorCuenta cuentas={catalogo.cuentas} tipos={['costo', 'gasto']} value={null} onChange={(id) => setLineas(prev => prev.map(l => ({ ...l, cuentaId: id })))} placeholder="Elegir…" compacto /></div>
              </div>
            )}
            {!isFromCotizacion && (
              <button onClick={addLinea} className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
                <Plus size={14} /> Agregar línea
              </button>
            )}
          </div>

          <div style={{overflow:'visible'}}>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 border">
                  {isFromCotizacion && <th className="px-2 py-2 w-10 border"></th>}
                  <th className="px-3 py-2 text-left border w-24">Código</th>
                  <th className="px-3 py-2 text-left border">Descripción</th>
                  <th className="px-3 py-2 text-center border w-20">Cant.</th>
                  {isFromCotizacion && <th className="px-3 py-2 text-right border w-24">P. Venta</th>}
                  <th className="px-3 py-2 text-right border w-28">Costo Unit. *</th>
                  <th className="px-3 py-2 text-center border w-20">IVA %</th>
                  <th className="px-3 py-2 text-right border w-24">IVA</th>
                  <th className="px-3 py-2 text-right border w-28">Total</th>
                  {catalogo && <th className="px-3 py-2 text-left border w-64">Cuenta presupuestaria</th>}
                  {!isFromCotizacion && <th className="px-2 py-2 w-10 border"></th>}
                </tr>
              </thead>
              <tbody>
                {lineas.map(l => {
                  const sub = (parseFloat(l.cantidad) || 0) * (parseFloat(l.costoUnitario) || 0);
                  const iva = Math.round(sub * (parseFloat(l.impuestoTarifa) || 0) / 100 * 100) / 100;
                  const total = sub + iva;
                  const isSelected = selectedLineas.has(l.id);

                  return (
                    <tr key={l.id} className={`border ${isFromCotizacion && !isSelected ? 'opacity-40 bg-gray-50' : ''}`}>
                      {isFromCotizacion && (
                        <td className="px-2 py-1 border text-center">
                          <input type="checkbox" checked={isSelected} onChange={() => toggleLinea(l.id)} className="h-4 w-4 rounded" />
                        </td>
                      )}
                      <td className="px-2 py-1 border">
                        <input type="text" value={l.codigo} onChange={(e) => updateLinea(l.id, 'codigo', e.target.value)}
                          className="w-full px-1 py-1 border rounded text-xs" placeholder="Código" />
                      </td>
                      <td className="px-2 py-1 border" style={{position:'relative', overflow:'visible'}}>
                        <input type="text" value={activeLineId === l.id ? productoSearch : l.detalle}
                          onChange={(e) => {
                            if (!isFromCotizacion) {
                              setActiveLineId(l.id);
                              setProductoSearch(e.target.value);
                              updateLinea(l.id, 'detalle', e.target.value);
                            } else {
                              updateLinea(l.id, 'detalle', e.target.value);
                            }
                          }}
                          onFocus={() => { if (!isFromCotizacion) { setActiveLineId(l.id); setProductoSearch(l.detalle); } }}
                          onBlur={() => setTimeout(() => { setActiveLineId(null); setProductoResults([]); }, 200)}
                          className="w-full px-1 py-1 border rounded text-xs" placeholder="Buscar por nombre o código..." />
                        {activeLineId === l.id && productoResults.length > 0 && (
                          <div className="absolute z-50 left-0 mt-1 bg-white border rounded-lg shadow-xl max-h-60 overflow-y-auto" style={{width:'400px'}}>
                            {productoResults.map(p => (
                              <button key={p.id} onMouseDown={() => selectProducto(p, l.id)}
                                className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm border-b last:border-0 flex items-center gap-3">
                                <span className="text-xs text-gray-400 font-mono w-20 shrink-0">{p.sku || p.codigo || '-'}</span>
                                <span className="font-medium truncate">{p.nombre || p.detalle}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1 border">
                        <input type="number" value={l.cantidad} onChange={(e) => updateLinea(l.id, 'cantidad', e.target.value)}
                          className="w-full px-1 py-1 border rounded text-xs text-center" min="0" step="1" />
                      </td>
                      {isFromCotizacion && (
                        <td className="px-2 py-1 border text-right text-xs text-gray-400">
                          {l.precioVenta ? `${sym}${parseFloat(l.precioVenta).toFixed(2)}` : '-'}
                        </td>
                      )}
                      <td className="px-2 py-1 border">
                        <input type="number" value={l.costoUnitario} onChange={(e) => updateLinea(l.id, 'costoUnitario', e.target.value)}
                          className="w-full px-1 py-1 border rounded text-xs text-right font-medium" min="0" step="0.01" placeholder="0.00" />
                      </td>
                      <td className="px-2 py-1 border">
                        <input type="number" value={l.impuestoTarifa} onChange={(e) => updateLinea(l.id, 'impuestoTarifa', e.target.value)}
                          className="w-full px-1 py-1 border rounded text-xs text-center" min="0" step="1" />
                      </td>
                      <td className="px-2 py-1 border text-right text-xs">{sym}{iva.toFixed(2)}</td>
                      <td className="px-2 py-1 border text-right text-xs font-semibold">{sym}{total.toFixed(2)}</td>
                      {catalogo && (
                        <td className="px-2 py-1 border space-y-1">
                          <SelectorCuenta cuentas={catalogo.cuentas} tipos={['costo', 'gasto']} value={l.cuentaId} onChange={(id) => updateLinea(l.id, 'cuentaId', id)} placeholder="Sin cuenta" compacto />
                          <SelectorCentro centros={catalogo.centros} value={l.centroCostoId} onChange={(id) => updateLinea(l.id, 'centroCostoId', id)} compacto />
                        </td>
                      )}
                      {!isFromCotizacion && (
                        <td className="px-2 py-1 border text-center">
                          <button onClick={() => removeLinea(l.id)} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totals */}
        <div className="flex justify-end mb-4">
          <div className="bg-gray-50 rounded-lg p-4 min-w-[250px]">
            <div className="flex justify-between text-sm mb-1"><span>Subtotal:</span><span className="font-medium">{sym}{totals.subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between text-sm mb-1"><span>Impuesto:</span><span>{sym}{totals.impuesto.toFixed(2)}</span></div>
            <div className="flex justify-between text-base font-bold border-t pt-2"><span>Total:</span><span className="text-amber-700">{sym}{totals.total.toFixed(2)}</span></div>
          </div>
        </div>

        {/* Notes */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
          <textarea value={form.notas} onChange={(e) => setForm(prev => ({ ...prev, notas: e.target.value }))}
            rows={2} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Notas adicionales..." />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button onClick={() => navigate('/compras/ordenes-compra')} className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50">
            Cancelar
          </button>
          <button onClick={() => handleSave()} disabled={saving}
            className="flex items-center gap-2 px-6 py-2 border-2 border-amber-600 text-amber-600 rounded-lg hover:bg-amber-50 disabled:opacity-50 font-medium">
            <Save size={16} /> {saving ? 'Guardando...' : isEditing ? 'Guardar Cambios' : 'Crear Orden de Compra'}
          </button>
        </div>
      </div>

      <ModalExcedido excedido={excedido} saving={saving} onCerrar={() => setExcedido(null)} onAutorizar={(m) => handleSave(m)} />
    </div>
  );
}
